// Translate between Configure's flat React state (70+ useState slices) and the structured
// project payload sent to the backend. Pure functions — no React imports.
//
// Both directions are tolerant: missing fields fall back to the matching slice in
// DEFAULT_PROJECT_STATE so older saved payloads still load cleanly.
import { DEFAULT_PROJECT_STATE } from '../constants/defaultConfig';

const pickClientDetails = (d = {}) => ({
  name:         d.name         || '',
  phone:        d.phone        || '',
  email:        d.email        || '',
  propertyType: d.propertyType || '',
  address:      d.address      || '',
  dateOfVisit:  d.dateOfVisit  || '',
});

// Consultant details default to the logged-in user's own profile (Settings > Update
// Profile) so every project's cover page stays in sync when the profile changes —
// unless a project explicitly overrides them with its own saved values.
const getProfileFallback = () => {
  try {
    const u = JSON.parse(localStorage.getItem('user_data') || 'null');
    if (!u) return {};
    return { name: u.name || '', phone: u.mobile || '', email: u.email || '', address: u.address || '' };
  } catch {
    return {};
  }
};

const pickConsultantDetails = (d = {}) => {
  const fallback = getProfileFallback();
  return {
    name:    d.name    || fallback.name    || '',
    phone:   d.phone   || fallback.phone   || '',
    email:   d.email   || fallback.email   || '',
    address: d.address || fallback.address || '',
  };
};

// state -> wire payload (used for create + edit)
export const buildProjectPayload = (editorState, meta = {}) => ({
  name:         meta.name         || 'Untitled Project',
  floorPlanRef: meta.floorPlanRef ?? null,
  unit:         editorState.currentUnit    ?? DEFAULT_PROJECT_STATE.unit,
  pixelsPerUnit: editorState.pixelsPerUnit ?? DEFAULT_PROJECT_STATE.pixelsPerUnit,
  isCalibrationSet: !!editorState.isCalibrationSet,

  config: {
    polygon: {
      points:           editorState.polygonPoints           ?? [],
      color:            editorState.polygonColor            ?? DEFAULT_PROJECT_STATE.polygon.color,
      lineWeight:       editorState.polygonLineWeight       ?? DEFAULT_PROJECT_STATE.polygon.lineWeight,
      lineOpacity:      editorState.polygonLineOpacity      ?? DEFAULT_PROJECT_STATE.polygon.lineOpacity,
      opacity:          editorState.polygonOpacity          ?? DEFAULT_PROJECT_STATE.polygon.opacity,
      showLayer:        !!editorState.showPolygonLayer,
      showFill:         !!editorState.showPolygonFill,
      showPoints:       !!editorState.showPolygonPoints,
      showLines:        editorState.showPolygonLines !== undefined ? !!editorState.showPolygonLines : true,
      showCenterPoint:  !!editorState.showCenterPoint,
    },

    measurements: {
      items:      editorState.measurements ?? [],
      lineColor:  editorState.measurementLineColor  ?? DEFAULT_PROJECT_STATE.measurements.lineColor,
      lineWeight: editorState.measurementLineWeight ?? DEFAULT_PROJECT_STATE.measurements.lineWeight,
      labelColor: editorState.measurementLabelColor ?? DEFAULT_PROJECT_STATE.measurements.labelColor,
    },

    compass: {
      center:           editorState.compassCenter        ?? null,
      rotation:         editorState.compassRotation      ?? 0,
      opacity:          editorState.compassOpacity       ?? DEFAULT_PROJECT_STATE.compass.opacity,
      lineOpacity:      editorState.compassLineOpacity   ?? DEFAULT_PROJECT_STATE.compass.lineOpacity,
      lineWeight:       editorState.compassLineWeight    ?? DEFAULT_PROJECT_STATE.compass.lineWeight,
      color:            editorState.compassColor         ?? DEFAULT_PROJECT_STATE.compass.color,
      showCompass:      !!editorState.showCompass,
      showCompassLines: editorState.showCompassLines !== undefined ? !!editorState.showCompassLines : true,
      showSectorAreas:  editorState.showSectorAreas !== undefined ? !!editorState.showSectorAreas : true,
      show32Directions: !!editorState.show32Directions,
      lines:            editorState.compassLines ?? [],
      angleInputs:      editorState.angleInputs  ?? [],
    },

    grid: {
      showGrid: !!editorState.showGrid,
      opacity:  editorState.gridOpacity ?? DEFAULT_PROJECT_STATE.grid.opacity,
      ratioX:   editorState.gridRatioX  ?? 1,
      ratioY:   editorState.gridRatioY  ?? 1,
    },

    rooms: {
      roomLayers: editorState.roomLayers ?? [],
    },

    entrance: {
      entrancePoints: editorState.entrancePoints ?? [],
      entranceData:   editorState.entranceData   ?? null,
    },

    // layerImages holds rendered PNG/data-URLs per layer; persisting them is what allows
    // the report to render directly after a project reload (without re-opening each layer).
    // Tradeoff: payload size grows with the number of enabled layers.
    layers: {
      exportLayers:      editorState.exportLayers      ?? DEFAULT_PROJECT_STATE.layers.exportLayers,
      savedLayerConfigs: editorState.savedLayerConfigs ?? {},
      layerImages:       editorState.layerImages       ?? {},
    },

    coverPage: {
      // meta.clientDetails (passed directly at save time, e.g. from the "Create
      // Project" form) wins over the live clientDetails state slice — avoids a
      // stale-closure race where setClientDetails(...) was just called but this
      // save's own snapshot was captured before that state update landed.
      clientDetails:     pickClientDetails(meta.clientDetails ?? editorState.clientDetails),
      consultantDetails: pickClientDetails(editorState.consultantDetails),
      selected45Block:   editorState.selected45Block ?? null,
      // Footer line printed on every report page ('' = use the user's profile details).
      footerText:        editorState.reportFooterText ?? '',
    },

    // Report data — saved per-project so switching projects (or opening on a
    // different device/browser) restores the correct data instead of relying
    // on the browser's own localStorage as the source of truth.
    reports: {
      vastuDevtaGuide:     meta.reportData?.vastuDevtaGuide     ?? null,
      spaceAnalysis:       meta.reportData?.spaceAnalysis       ?? null,
      entranceAnalysis:    meta.reportData?.entranceAnalysis    ?? null,
      numeroVastuRemedies: editorState.numeroRemedyData         ?? null,
      vastuPersons:        meta.reportData?.vastuPersons        ?? null,
      numeroVastu:         meta.reportData?.numeroVastu         ?? null,
      numeroVastuKua:      meta.reportData?.numeroVastuKua      ?? null,
      astroVastu:          meta.reportData?.astroVastu          ?? null,
      cutZones:            meta.reportData?.cutZones            ?? null,
      extensionZones:      meta.reportData?.extensionZones      ?? null,
      navgrahShanti:       meta.reportData?.navgrahShanti       ?? null,
      fengShui:            meta.reportData?.fengShui            ?? null,
      helix:               meta.reportData?.helix               ?? null,
      zoneAnalysis:        meta.reportData?.zoneAnalysis        ?? null,
    },
  },
});

// wire payload -> flat setter inputs (used for load).
// Returns an object whose keys map 1:1 to the setter names used inside Configure.
export const hydrateEditorState = (project) => {
  const c = project?.config ?? {};
  const D = DEFAULT_PROJECT_STATE;

  const polygon       = c.polygon       ?? D.polygon;
  const measurements  = c.measurements  ?? D.measurements;
  const compass       = c.compass       ?? D.compass;
  const grid          = c.grid          ?? D.grid;
  const rooms         = c.rooms         ?? D.rooms;
  const entrance      = c.entrance      ?? D.entrance;
  const layers        = c.layers        ?? D.layers;
  const coverPage     = c.coverPage     ?? D.coverPage;

  return {
    currentUnit:        project?.unit          ?? D.unit,
    pixelsPerUnit:      project?.pixelsPerUnit ?? D.pixelsPerUnit,
    isCalibrationSet:   project?.isCalibrationSet ?? (Number(project?.pixelsPerUnit) > 0),

    polygonPoints:      polygon.points       ?? [],
    polygonColor:       polygon.color        ?? D.polygon.color,
    polygonLineWeight:  polygon.lineWeight   ?? D.polygon.lineWeight,
    polygonLineOpacity: polygon.lineOpacity  ?? D.polygon.lineOpacity,
    polygonOpacity:     polygon.opacity      ?? D.polygon.opacity,
    showPolygonLayer:   !!polygon.showLayer,
    showPolygonFill:    !!polygon.showFill,
    showPolygonPoints:  !!polygon.showPoints,
    showPolygonLines:   polygon.showLines !== undefined ? !!polygon.showLines : true,
    showCenterPoint:    !!polygon.showCenterPoint,
    isPolygonComplete:  Array.isArray(polygon.points) && polygon.points.length >= 3,

    measurements:           measurements.items      ?? [],
    measurementLineColor:   measurements.lineColor  ?? D.measurements.lineColor,
    measurementLineWeight:  measurements.lineWeight ?? D.measurements.lineWeight,
    measurementLabelColor:  measurements.labelColor ?? D.measurements.labelColor,

    compassCenter:    compass.center        ?? null,
    compassRotation:  compass.rotation      ?? 0,
    compassOpacity:   compass.opacity       ?? D.compass.opacity,
    compassLineOpacity: compass.lineOpacity ?? D.compass.lineOpacity,
    compassLineWeight: compass.lineWeight   ?? D.compass.lineWeight,
    compassColor:     compass.color         ?? D.compass.color,
    showCompass:      !!compass.showCompass,
    showCompassLines: compass.showCompassLines !== undefined ? !!compass.showCompassLines : true,
    showSectorAreas:  compass.showSectorAreas  !== undefined ? !!compass.showSectorAreas  : true,
    show32Directions: !!compass.show32Directions,
    compassLines:     compass.lines        ?? [],
    angleInputs:      compass.angleInputs  ?? [],

    showGrid:    !!grid.showGrid,
    gridOpacity: grid.opacity ?? D.grid.opacity,
    gridRatioX:  grid.ratioX  ?? 1,
    gridRatioY:  grid.ratioY  ?? 1,

    roomLayers:     rooms.roomLayers       ?? [],
    entrancePoints: entrance.entrancePoints ?? [],
    entranceData:   entrance.entranceData   ?? null,

    exportLayers:      layers.exportLayers      ?? D.layers.exportLayers,
    savedLayerConfigs: layers.savedLayerConfigs ?? {},
    layerImages:       layers.layerImages       ?? {},

    clientDetails:     pickClientDetails(coverPage.clientDetails),
    consultantDetails: pickConsultantDetails(coverPage.consultantDetails),
    selected45Block:   coverPage.selected45Block ?? null,
    reportFooterText:  coverPage.footerText ?? '',

    numeroRemedyData:  (c.reports ?? {}).numeroVastuRemedies ?? {},
  };
};
